// Staff nameplate colors for the OR board.
//
// Each staffed room shows the DOCTOR in a big colored box and, to its right, the
// CRNA in a smaller nameplate. Colors are chosen automatically from each person's
// shift (see the two functions below). The palette itself — and the user's own
// per-machine overrides, editable from the Color Legend — lives in nameplatePalette.ts.

import type { CSSProperties } from 'react';
import { utcToLocalTime } from '../utils/timezone.ts';
import { NAMEPLATE_COLORS as PALETTE, PALETTE_DEFAULTS as DEFAULTS } from './nameplatePalette.ts';

// ---- The palette ------------------------------------------------------------
// The colours themselves (and the user's per-machine overrides) live in nameplatePalette.ts, which
// also owns the Color Legend's editable-category list. Re-exported here so the many existing
// `import { NAMEPLATE_COLORS } from './staffNameplates.ts'` call sites keep working unchanged.
export { NAMEPLATE_COLORS, PALETTE_DEFAULTS, matchesPaletteColor } from './nameplatePalette.ts';
export type { PaletteKey } from './nameplatePalette.ts';
const PALETTE_DEFAULTS_NERVE = DEFAULTS.nerveBlockBorder;
const PALETTE_DEFAULTS_PEDS = DEFAULTS.pediatricBorder;

// A room's later cases with no explicit staffing are assumed covered by the team from the case
// before them, shown in this green (an "assumed continuation" cue).
export const INHERITED_TEAM_COLOR = '#2e9e5b';

// Recolor swatches offered on a person's card, scoped to their ROLE (a doctor only gets
// doctor-meaningful colors, a CRNA only CRNA colors, a student only competency colors). Each
// swatch's hover label mirrors the Color Legend so the meaning is identical in both places.
//
// This is a FUNCTION on purpose. A module-level array of palette colours would freeze them at import
// and keep showing the shipped colour after the user recolours a category from the legend. (There
// used to be exactly such an array here, RECOLOR_OPTIONS; it had no callers and was deleted rather
// than left as a trap.)

// Recolor swatches offered on a person's card, scoped to their ROLE (a doctor only gets
// doctor-meaningful colors, a CRNA only CRNA colors, a student only competency colors).
export function recolorOptionsFor(employeeType?: string): { label: string; color: string }[] {
  if (employeeType === 'doctor') return [
    { label: 'Supervising CRNA(s)', color: PALETTE.docSupervise },
    { label: 'Solo in a room', color: PALETTE.docSolo },
    { label: 'Out 3pm', color: PALETTE.docOut3pm },
    { label: 'Out 5pm', color: PALETTE.docOut5pm },
    { label: 'Out 7pm', color: PALETTE.docOut7pm },
    { label: 'Request case', color: PALETTE.request },
  ];
  if (employeeType && employeeType.startsWith('nurse-student')) return [
    { label: 'New student', color: PALETTE.studentNew },
    { label: 'Intermediate student', color: PALETTE.studentIntermediate },
    { label: 'Green (senior) student', color: PALETTE.studentGreen },
  ];
  // CRNA (nurse)
  return [
    { label: 'Out 3pm', color: PALETTE.crnaOut3pm },
    { label: 'Out 5pm', color: PALETTE.crnaOut5pm },
    { label: 'Out 7pm / 3–11pm', color: PALETTE.crnaOut7pm },
    { label: '11pm–7am', color: PALETTE.crnaOvernight },
    { label: 'Request case', color: PALETTE.request },
  ];
}

// Student nameplate/brick color by competency level (slice 2).
export function studentNameplateColor(level?: string): string {
  switch (level) {
    case 'new': return PALETTE.studentNew;
    case 'intermediate': return PALETTE.studentIntermediate;
    case 'green': return PALETTE.studentGreen;
    default: return PALETTE.studentDefault;
  }
}

// A senior student IS the green tier in this app (green = "can run solo at 2:1 supervision"), so a
// 'nurse-student-senior' always reads GREEN even when its competency field was never set (the common case).
// Any other student type falls back to its explicit competency level.
export function studentColorFor(employeeType?: string, competency?: string): string {
  if (employeeType === 'nurse-student-senior') return PALETTE.studentGreen;
  return studentNameplateColor(competency);
}

// Rule 6: rooms whose case needs a nerve block get a yellow border. Keyword list mirrors the
// backend Symphony detector (extend as real case data arrives). This is the AUTO detection; a case
// can also be hand-flagged on the board (see caseMarkers.ts), which overrides the keyword result.
// DEFAULT only. The live, user-editable value is PALETTE.nerveBlockBorder -- an `export const`
// freezes at import and would never see an edit, so read the palette at the point of use.
export const NERVE_BLOCK_BORDER = PALETTE_DEFAULTS_NERVE;
// Lowercase substrings of the lowercased case text. "biceps tendonesis" sits beside the correct spelling "tenodesis".
// MUST MATCH backend/src/allocate-schedules-symphony.ts. Braden 2026-09-19 added bare `knee` and `shoulder` there;
// this copy was missed, so for a day the board drew no nerve-block border on 464 cases Symphony was already
// treating as nerve blocks (and computed their report-in at 30 minutes instead of 45). Two hand-synced copies is
// the real defect — see §35 item 7.
const NERVE_BLOCK_KEYWORDS = ['knee', 'shoulder', 'total shoulder', 'total knee', 'knee arthroplasty', 'shoulder arthroscopy', 'acl repair', 'acl reconstruction', 'biceps tenodesis', 'biceps tendonesis'];
export function needsNerveBlock(surgeryType?: string): boolean {
  if (!surgeryType) return false;
  const lower = surgeryType.toLowerCase();
  return NERVE_BLOCK_KEYWORDS.some(k => lower.includes(k));
}

// Pediatric cases (age ≤ 12) get a hot-pink border. We deliberately do not collect or persist
// patient age from customer data (see backend/src/utils/jsonp-parser.ts), so this is MANUAL-ONLY:
// a case is flagged pediatric by hand on the board (see caseMarkers.ts), never auto-detected. A case
// can carry BOTH borders: a yellow nerve-block border and a pink pediatric border at the same time.
// DEFAULT only -- see NERVE_BLOCK_BORDER above. Live value: PALETTE.pediatricBorder.
export const PEDIATRIC_BORDER = PALETTE_DEFAULTS_PEDS;
export const PEDIATRIC_AGE_MAX = 12;

// Minimal shift shape we read (matches the board's shift objects).
export interface NameplateShift {
  startTimeUtc?: string;
  endTimeUtc?: string;
  order?: number | null; // doctor pecking order; absent/null => shift worker
}

// Local hour (0–23) of a UTC timestamp, in the viewer's timezone (same basis the board uses).
function localHourOf(utc?: string): number {
  if (!utc) return NaN;
  const h = parseInt(utcToLocalTime(utc).slice(0, 2), 10);
  return h === 24 ? 0 : h;
}

// CRNA fill color from their shift (Rule 6 palette).
export function crnaNameplateColor(shift?: NameplateShift | null): string {
  if (!shift || !shift.endTimeUtc) return PALETTE.crnaDefault;
  const startH = localHourOf(shift.startTimeUtc);
  const endHRaw = localHourOf(shift.endTimeUtc);
  // A shift ending at midnight (00:00) is end-of-day, not 3am — treat hour 0 as 24 so it
  // doesn't fall into the "out by 3pm" bucket below.
  const endH = endHRaw === 0 ? 24 : endHRaw;
  if (!isNaN(startH) && (startH >= 22 || startH < 5)) return PALETTE.crnaOvernight; // 11pm–7am
  if (endH <= 15) return PALETTE.crnaOut3pm;  // until 3pm
  if (endH <= 17) return PALETTE.crnaOut5pm;  // until 5pm
  return PALETTE.crnaOut7pm;                   // 7pm, or the 3pm–11pm evening shift
}

/**
 * The shift-end bucket for a doctor, mirroring crnaNameplateColor's thresholds. Returns undefined for
 * a pecking-order doctor (order set) or when there is no end time to read.
 *
 * THRESHOLDS, not exact matches. The previous rule tested `endH === 15 || endH === 17`, so a shift
 * ending at 14:00 fell through to no shift-end colour at all, and 19:00 had no bucket to fall into.
 */
function doctorShiftEndColor(shift: NameplateShift | null | undefined): string | undefined {
  const isShiftWorker = !shift || shift.order === undefined || shift.order === null;
  if (!isShiftWorker || !shift || !shift.endTimeUtc) return undefined;
  const endHRaw = localHourOf(shift.endTimeUtc);
  if (isNaN(endHRaw)) return undefined;
  const endH = endHRaw === 0 ? 24 : endHRaw;  // midnight is end-of-day, not 3am (as for CRNAs)
  if (endH <= 15) return PALETTE.docOut3pm;
  if (endH <= 17) return PALETTE.docOut5pm;
  return PALETTE.docOut7pm;
}

/**
 * Doctor fill color. A SHIFT WORKER reads as their shift end (out 3pm / 5pm / 7pm), exactly as a CRNA
 * does; a PECKING-ORDER doctor has no shift end to show, so they read supervising vs. alone.
 *
 * The shift-end colour taking precedence over the supervised count is deliberate and is what makes the
 * three buckets visible on the board at all. It does mean a shift-worker doctor's plate says WHEN THEY
 * LEAVE rather than what they are doing; supervise-blue / solo-fuchsia now describe pecking-order
 * doctors (and anyone with no shift). Braden 2026-10-03.
 */
export function doctorNameplateColor(shift: NameplateShift | null | undefined, supervisedCrnaCount: number): string {
  const byShiftEnd = doctorShiftEndColor(shift);
  if (byShiftEnd) return byShiftEnd;
  if (supervisedCrnaCount >= 1) return PALETTE.docSupervise;  // supervising CRNA(s)
  return PALETTE.docSolo;                                     // alone in a room
}

// Doctor color for the bullpen roster (before placement, so there's no supervised count yet): a shift
// worker reads as their shift end (out 3pm / 5pm / 7pm); a pecking-order doctor — or one with no shift
// — gets the light-blue baseline.
export function doctorBullpenColor(shift?: NameplateShift | null): string {
  return doctorShiftEndColor(shift) ?? PALETTE.docSupervise;
}

// Box styling — the doctor box is deliberately bigger/bolder than the CRNA plate.
export function doctorPlateStyle(background: string): CSSProperties {
  return {
    background, color: PALETTE.text, fontWeight: 700, fontSize: '0.82em',
    padding: '3px 9px', borderRadius: '6px', whiteSpace: 'nowrap', lineHeight: 1.25,
    letterSpacing: '0.2px', boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
  };
}
export function crnaPlateStyle(background: string): CSSProperties {
  return {
    background, color: PALETTE.text, fontWeight: 600, fontSize: '0.76em',
    padding: '2px 7px', borderRadius: '6px', whiteSpace: 'nowrap', lineHeight: 1.25,
  };
}
